import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Auth } from 'aws-amplify';
import { tagForm } from 'tagManager';

import { AppRoutes } from 'routes';

import ErrorView from '../../components/errors/ErrorView';
import forgotcredentialErrors from '../../components/errors/forgotcredentialErrors';
import PasswordConfirmation from '../../components/inputs/PasswordConfirmation';
import VerificationCodeInput from '../../components/inputs/VerificationCode';
import { ModalSto, PrimaryButtonSto, TextButtonSto } from '../../components/sto';
import useFormFields from '../../hooks/useFormFields';
import useLocalization from '../../hooks/useLocalization';
import scrollTo from '../../utils/scrollTo';
import OTPModal from '../modals/OTPResentModal';
import OverlaySpinner from '../spinner/OverlaySpinner';

const generalMessageKey = 'general.unexpectedErrorOccurred';

function UpdatePassword(props) {
    const { localizedPush } = useLocalization();
    const { formFields, setFormField } = useFormFields({
        password: '',
        verificationCode: '',
    });
    const [isFormValid, setFormValidity] = useState(false);
    const [error, setError] = useState(null);
    const [displayModal, updateModalVisibility] = useState(null);
    const [otpSent, isOTPSent] = useState(true);
    const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

    const { password, verificationCode } = formFields;

    useEffect(() => {
        setFormValidity(verificationCode.isValid && password.isValid);
    }, [verificationCode, password]);

    const onVerificationCodeChange = (input) => {
        setFormField('verificationCode', input);
    };

    const onPasswordChange = (password) => {
        setFormField('password', {
            isValid: password.passwordState,
            value: password.passwordValue,
        });
    };

    const handleSubmitClick = async (e) => {
        e.preventDefault();
        scrollTo(0, 0);
        setError(null);
        setIsUpdatingPassword(true);
        try {
            await Auth.forgotPasswordSubmit(props.username, verificationCode.value, password.value);
            tagForm({
                fields: {
                    verification_code: verificationCode.value,
                },
                name: 'reset-password-step2',
                success: true,
            });
            updateModalVisibility('resetsuccess');
        } catch (error) {
            const errorMessage = forgotcredentialErrors[error.code];
            setError(errorMessage || generalMessageKey);
            tagForm({
                fields: {
                    verification_code: verificationCode.value,
                },
                message: error.message,
                name: 'reset-password-step2',
                success: false,
            });
        } finally {
            setIsUpdatingPassword(false);
        }
    };

    const resendCode = async () => {
        setError(null);
        try {
            await Auth.forgotPassword(props.username);
            updateModalVisibility('otp');
            isOTPSent(true);
        } catch (e) {
            isOTPSent(false);
            const errorMessage = forgotcredentialErrors[e.code];
            setError(errorMessage || generalMessageKey);
        }
    };

    const closeModal = () => {
        if (displayModal === 'resetsuccess') {
            localizedPush(AppRoutes.baseRoute);
        }
        updateModalVisibility(null);
    };

    return (
        <div>
            <ErrorView error={error} />
            {isUpdatingPassword && <OverlaySpinner />}
            <OTPModal email={props.email} onOk={closeModal} otpSent={otpSent} visible={displayModal === 'otp'} />

            <ModalSto
                buttonSize="large"
                isCTA={false}
                modalType="resetPassword"
                okText="general.ok"
                onOk={closeModal}
                visible={displayModal === 'resetsuccess'}
                wrapClassName="account-validated-modal"
            >
                <h3>
                    {' '}
                    <FormattedMessage id="pages.resetpassword.success" />
                </h3>
            </ModalSto>

            <form onSubmit={handleSubmitClick}>
                <PasswordConfirmation
                    onChange={onPasswordChange}
                    password={{
                        passwordState: password.isValid,
                        passwordValue: password.value,
                    }}
                />

                <VerificationCodeInput
                    field={verificationCode}
                    onBlur={onVerificationCodeChange}
                    onChange={onVerificationCodeChange}
                />

                <div className="pprlpay-link-container">
                    <p>
                        <FormattedMessage id="pages.forgotPassword.resendCode" />
                    </p>
                    <TextButtonSto
                        className="button-link sto-links"
                        id="page-resetPassword-button-resendCode"
                        labelKey="pages.forgotPassword.resendCodeLink"
                        onClick={resendCode}
                        style={{ marginBottom: '10px' }}
                        type="button"
                    />
                </div>

                <div>
                    <div className="wrapper-button-sto-container">
                        <PrimaryButtonSto disabled={!isFormValid} labelKey="pages.button.submit" type="submit" />
                    </div>
                </div>
            </form>
        </div>
    );
}
export default UpdatePassword;
